'use client';

import { CouponInfo } from '@otter-ai/recall-client';
import { useCheckout } from '@stripe/react-stripe-js';
import {
  addDays,
  addMonths,
  addYears,
  differenceInDays,
  format,
  fromUnixTime,
} from 'date-fns';
import { useMemo } from 'react';

const createIntervalFunctions = (baseDate: Date, count: number) =>
  ({
    day: () => addDays(baseDate, count),
    week: () => addDays(baseDate, count * 7),
    month: () => addMonths(baseDate, count),
    year: () => addYears(baseDate, count),
  }) as const;

export function getTrialPeriodDays(couponInfo: CouponInfo): number {
  const hasCycleDiscount =
    couponInfo.discount_type === 'cycle' && couponInfo.period_amount;
  const hasCyclePercentTrial =
    couponInfo.discount_type === 'cycle_percent' &&
    couponInfo.trial_period_amount;

  if (!(hasCycleDiscount || hasCyclePercentTrial)) {
    return 0;
  }

  const amount = hasCycleDiscount
    ? (couponInfo.period_amount ?? 0)
    : (couponInfo.trial_period_amount ?? 0);
  const type = hasCycleDiscount
    ? couponInfo.period_type
    : couponInfo.trial_period_type;

  const today = new Date();

  const intervalFunctions = createIntervalFunctions(today, amount);
  const addFunction = intervalFunctions[type as keyof typeof intervalFunctions];

  if (!addFunction) {
    return 0;
  }

  const endDate = addFunction();
  return differenceInDays(endDate, today);
}

export function useBillingDates() {
  const { recurring } = useCheckout();
  const commonDateFormat = 'MMMM d, yyyy';

  function getAutoRenewDate(): Date {
    if (!recurring?.interval || !recurring?.intervalCount) {
      throw new Error('Invalid recurring configuration');
    }

    const today = new Date();
    const intervalFunctions = createIntervalFunctions(
      today,
      recurring.intervalCount,
    );

    const addFunction =
      intervalFunctions[recurring.interval as keyof typeof intervalFunctions];

    if (!addFunction) {
      throw new Error(`Invalid interval: ${recurring.interval}`);
    }

    return addFunction();
  }

  const autoRenewDate = format(getAutoRenewDate(), commonDateFormat);
  const { autoRenewPostTrialEnd, trialEndDate } = useMemo(() => {
    if (!recurring?.trial?.trialEnd)
      return {
        autoRenewPostTrialEnd: '',
        trialEndDate: '',
      };
    const lastTrialDay = fromUnixTime(recurring.trial.trialEnd);
    const intervalFunctions = createIntervalFunctions(
      lastTrialDay,
      recurring.intervalCount,
    );

    const addCycle =
      intervalFunctions[recurring.interval as keyof typeof intervalFunctions];

    return {
      autoRenewPostTrialEnd: format(addCycle(), commonDateFormat),
      trialEndDate: format(lastTrialDay, commonDateFormat),
    };
  }, [
    recurring?.trial?.trialEnd,
    recurring?.intervalCount,
    recurring?.interval,
  ]);

  const today = format(new Date(), commonDateFormat);

  return {
    autoRenewDate,
    getAutoRenewDate,
    createIntervalFunctions,
    autoRenewPostTrialEnd,
    today,
    commonDateFormat,
    trialEndDate,
  };
}
